import { Controller } from "stimulus"
import { validateSsn } from "validators/ssn_validator"
import { validateCitizenship } from "validators/citizenship_validator"
import { validateTribal } from "validators/tribal_validator"
import { applyResult, removeFieldErrorState, finalizeForm } from "validators/validation_display"

export default class extends Controller {

  connect() {
    if (document.documentElement.dataset.accessibleFormValidation !== 'true') return

    // valid submit sometimes disables continue button (to prevent double-submit)
    // turbolinks caches that disabled state in the page snapshot, so back-navigation would restore a stuck, disabled button
    // connect() runs on Turbolinks restore, so re-enable it whenever the page is (re)shown
    this.element.querySelectorAll('.manage-person-validations-click').forEach(btn => {
      btn.removeAttribute('disabled')
      btn.classList.remove('disabled')
    })

    // remove error state on radio groups when a selection is made
    this._radioChangeHandlers = []
    this.element.querySelectorAll('input[type="radio"]').forEach(radio => {
      const handler = () => removeFieldErrorState(radio)
      radio.addEventListener('change', handler)
      this._radioChangeHandlers.push({ radio, handler })
    })

    // clear a field's error as soon as the user edits it (text inputs, selects, textareas)
    this._fieldEditHandler = (event) => {
      const field = event.target
      if (!field || !field.matches) return
      if (field.classList.contains('tribe_codes')) { this.#clearTribeCodesError(); return }
      if (field.classList.contains('mask-ssn')) { this.#liveValidateSsnFormat(field); return }
      if (field.matches('input:not([type="radio"]), select, textarea')) {
        removeFieldErrorState(field)
      }
      // checking "No SSN" satisfies the SSN-required rule — clear the SSN field's surfaced error too
      if (field.matches('input[id*="no_ssn"]')) this.#clearSsnErrorOnNoSsn(field)
    }
    this.element.addEventListener('input', this._fieldEditHandler)
    this.element.addEventListener('change', this._fieldEditHandler)
  }

  disconnect() {
    this._radioChangeHandlers?.forEach(({ radio, handler }) => {
      radio.removeEventListener('change', handler)
    })
    this._radioChangeHandlers = []
    if (this._fieldEditHandler) {
      this.element.removeEventListener('input', this._fieldEditHandler)
      this.element.removeEventListener('change', this._fieldEditHandler)
      this._fieldEditHandler = null
    }
  }

  validateSsnFields() {
    const ssnInput = this.element.querySelector('.mask-ssn')
    const noSsnCheckbox = this.element.querySelector('input[id*="no_ssn"]')
    const ssnFacade = this.element.querySelector('.ssn-facade')

    // return if no SSN fields are present
    if (!ssnInput || !noSsnCheckbox) return

    // reset both fields, then apply the error (if any) to whichever the rule targets
    removeFieldErrorState(ssnInput)
    removeFieldErrorState(noSsnCheckbox)

    // hasStoredSsn is true when the user has a stored SSN that is masked by the UI
    // in that case, the input is blank but it's not valid to check "No SSN"
    const result = validateSsn({
      digits: ssnInput.value.replace(/\D/g, ''),
      noSsn: noSsnCheckbox.checked,
      hasStoredSsn: !!(ssnFacade && ssnFacade.innerHTML.trim() !== '')
    })

    if (!result.valid) {
      applyResult(result.target === 'noSsn' ? noSsnCheckbox : ssnInput, result)
    }
  }

  // base citizenship (us_citizen + conditional naturalized), incarceration, and tribal validation
  // only runs when applying for coverage
  // eligible-immigration status keeps banner "validation" (reminder, won't prevent submit)
  validateConsumerFields() {
    const usCitizenEl = this.element.querySelector('#us_citizen_true')
    const naturalizedEl = this.element.querySelector('#naturalized_citizen_true')
    const incarceratedEl = this.element.querySelector('#is_incarcerated_true')
    const tribeMemberEl = this.element.querySelector('#indian_tribe_member_yes')
    const tribalStateEl = this.element.querySelector('#tribal-state')
    const tribalNameEl = this.element.querySelector('#tribal-name')

    // reset error state on every field here before re-evaluating
    ;[usCitizenEl, naturalizedEl, incarceratedEl, tribeMemberEl, tribalStateEl, tribalNameEl]
      .forEach(el => removeFieldErrorState(el))
    this.#clearTribeCodesError()

    // do not run remaining validations if not applying for coverage, even if the fields are present
    if (!this.#isApplyingCoverage()) return

    // citizenship radio buttons — immigrationStatusRequired mirrors
    // EnrollRegistry.feature_enabled?(:immigration_status_question_required), surfaced as a data
    // attribute on the us_citizen_true radio by the view
    // when off, the immigration branch stays on the legacy reminder banner
    // read each group's value from the found element's own name so this works for
    // both consumer (person[...]) and FAA (applicant[...]) forms
    validateCitizenship({
      usCitizen: this.#radioValue(usCitizenEl?.name),
      naturalized: this.#radioValue(naturalizedEl?.name),
      immigrationStatusRequired: usCitizenEl?.dataset.immigrationStatusRequired === 'true'
    }).forEach(result => {
      if (result.valid) return
      if (result.target === 'naturalized' && !this.#isVisible(naturalizedEl)) return
      applyResult(result.target === 'naturalized' ? naturalizedEl : usCitizenEl, result)
    })

    // incarceration — required radio
    if (incarceratedEl && this.#radioValue(incarceratedEl.name) === null) {
      applyResult(incarceratedEl, { valid: false, target: 'isIncarcerated', code: 'incarceration_required' })
    }

    // tribal — the tribe-member question itself is required; sub-fields are validated when "yes".
    const isTribeMember = this.#radioValue(tribeMemberEl?.name)
    if (tribeMemberEl && isTribeMember === null) {
      applyResult(tribeMemberEl, { valid: false, target: 'tribeMember', code: 'tribe_member_required' })
    }

    // clear tribal fields if the user selects "no" for tribe member, so that they don't get validated
    if (isTribeMember === false) this.#clearTribalFields()

    const enrollState = this.element.querySelector('#enroll_state_abbr')?.value
    const featuredEnabled = this.element.querySelector('#is_featured_tribes_selection_enabled')?.value === 'true'
    const tribeCodeEls = Array.from(this.element.querySelectorAll('.tribe_codes'))
    validateTribal({
      isTribeMember: isTribeMember === true,
      tribalState: tribalStateEl?.value || '',
      tribalName: tribalNameEl?.value || '',
      featuredTribesInState: featuredEnabled && tribalStateEl?.value === enrollState,
      otherTribeSelected: tribeCodeEls.some(cb => cb.value === 'OT' && cb.checked),
      anyTribeSelected: tribeCodeEls.some(cb => cb.checked)
    }).forEach(result => {
      if (result.valid) return
      if (result.target === 'tribeCodes') this.#applyTribeCodesError(tribeCodeEls)
      else applyResult(result.target === 'tribalName' ? tribalNameEl : tribalStateEl, result)
    })
  }

  submit(event) {
    this.validateSsnFields()
    this.validateConsumerFields()

    // prevent submission if there are any validation errors
    // finalizeForm will also scroll the first invalid field into view and focus it for accessibility
    if (!finalizeForm(this.element)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // --- helpers ---

  // true | false | null (null = no radio of the group is selected)
  // retrieves the value of a radio button group by name, returning null if none are selected
  #radioValue(name) {
    const checked = this.element.querySelector(`input[name="${name}"]:checked`)
    if (!checked) return null
    return checked.value === 'true'
  }

  #isVisible(el) {
    return !!(el && el.offsetParent !== null)
  }

  #isApplyingCoverage() {
    const applying = this.element.querySelector('#applicant_is_applying_coverage_true, #is_applying_coverage_true')
    return applying ? applying.checked : true
  }

  #clearTribalFields() {
    const state = this.element.querySelector('#tribal-state')
    if (state) state.value = ''
    const name = this.element.querySelector('#tribal-name')
    if (name) name.value = ''
    this.element.querySelectorAll('.tribe_codes:checked').forEach(cb => { cb.checked = false })
  }

  // special handling for the featured-tribe checkboxes as they are a group
  // mark the group invalid via one box (blocks submit) and reveal the fieldset's invalid-feedback
  // feedbackFor can't resolve a checkbox group, so handle here
  #applyTribeCodesError(tribeCodeEls) {
    if (tribeCodeEls[0]) tribeCodeEls[0].setCustomValidity('invalid')
    const container = this.element.querySelector('.featured-tribe-container')
    container?.classList.add('is-invalid-group')
    const feedback = container?.querySelector('.invalid-feedback')
    if (feedback) feedback.classList.add('d-block')
  }

  #liveValidateSsnFormat(field) {
    if (field.value.trim() && field.validity.patternMismatch) {
      applyResult(field, { valid: false, target: 'ssn', code: 'ssn_required' })
    } else {
      removeFieldErrorState(field)
    }
  }

  // clear a previously surfaced SSN-required error when "No SSN" is checked
  // this will only run after the form has
  // 1. been validated (.was-validated) 
  // 2. the no_ssn box is checked
  // 3. the SSN field is in an error state. 
  // (connect() already early-returns when the flag is off, so the listener that calls this only exists on the accessible flow.)
  #clearSsnErrorOnNoSsn(checkbox) {
    if (!checkbox.checked) return
    if (!this.element.classList.contains('was-validated')) return
    const ssnInput = this.element.querySelector('.mask-ssn')
    if (ssnInput && !ssnInput.checkValidity()) removeFieldErrorState(ssnInput)
  }

  // removes special error state handling for the tribe code checkboxes
  #clearTribeCodesError() {
    this.element.querySelectorAll('.tribe_codes').forEach(cb => cb.setCustomValidity(''))
    const container = this.element.querySelector('.featured-tribe-container')
    container?.classList.remove('is-invalid-group')
    const feedback = container?.querySelector('.invalid-feedback')
    if (feedback) feedback.classList.remove('d-block')
  }
}
